import { Box, MeasurementUnit } from '../types/index.ts';
import Papa from 'papaparse';
import { v4 as uuidv4 } from 'uuid';

// Key to store data in local storage
const BOX_DATABASE_KEY = 'killian_box_database';

// A box is usable only if its dimensions are finite positive numbers.
const hasValidDimensions = (box: Box): boolean =>
  !!box &&
  !!box.dimensions &&
  Number.isFinite(box.dimensions.length) && box.dimensions.length > 0 &&
  Number.isFinite(box.dimensions.width) && box.dimensions.width > 0 &&
  Number.isFinite(box.dimensions.height) && box.dimensions.height > 0;

const saveBoxes = (boxes: Box[]): void => {
  try {
    localStorage.setItem(BOX_DATABASE_KEY, JSON.stringify(boxes));
  } catch (error) {
    // Quota exceeded or storage unavailable: keep the app alive.
    console.error('Box Finder: unable to persist the box database.', error);
  }
};

// Get all boxes. Corrupted or unexpected data never crashes the app.
export const getAllBoxes = (): Box[] => {
  let boxesJson: string | null;
  try {
    boxesJson = localStorage.getItem(BOX_DATABASE_KEY);
  } catch {
    // Storage blocked (private mode, sandboxed iframe, cookies disabled):
    // behave as an empty database instead of crashing the whole app.
    return [];
  }
  if (!boxesJson) {
    saveBoxes([]);
    return [];
  }
  try {
    const parsed = JSON.parse(boxesJson);
    if (!Array.isArray(parsed)) {
      saveBoxes([]);
      return [];
    }
    // Drop entries that would crash the UI (e.g. null dimensions saved by an old bug).
    const valid = parsed.filter((box: Box) => box && typeof box.id === 'string' && hasValidDimensions(box));
    if (valid.length !== parsed.length) saveBoxes(valid);
    return valid;
  } catch (error) {
    console.warn('Box Finder: unreadable box database in localStorage, resetting.', error);
    saveBoxes([]);
    return [];
  }
};

// Add a new box
export const addBox = (box: Box): void => {
  const boxes = getAllBoxes();
  const boxToAdd = { ...box, visible: box.visible !== undefined ? box.visible : true };
  boxes.push(boxToAdd);
  saveBoxes(boxes);
};

// Update an existing box
export const updateBox = (updatedBox: Box): void => {
  const boxes = getAllBoxes();
  const index = boxes.findIndex((box) => box.id === updatedBox.id);
  if (index !== -1) {
    if (updatedBox.visible === undefined && boxes[index].visible !== undefined) {
      updatedBox.visible = boxes[index].visible;
    }
    boxes[index] = updatedBox;
    saveBoxes(boxes);
  }
};

// Delete a box
export const deleteBox = (boxId: string): void => {
  saveBoxes(getAllBoxes().filter((box) => box.id !== boxId));
};

// Toggle box visibility
export const toggleBoxVisibility = (boxId: string): void => {
  const boxes = getAllBoxes();
  const index = boxes.findIndex((box) => box.id === boxId);
  if (index !== -1) {
    boxes[index].visible = !(boxes[index].visible !== false);
    saveBoxes(boxes);
  }
};

// Get only visible boxes (for packaging calculation)
export const getVisibleBoxes = (): Box[] => getAllBoxes().filter((box) => box.visible !== false);

// --- Mode démo : boîtes d'exemple -------------------------------------------
// Quand la base est vide, on injecte deux vraies boîtes utilisables pour tester
// l'outil. Elles portent `isExample: true` et sont retirées automatiquement dès
// que l'utilisateur enregistre ses propres boîtes (voir reconcileExampleBoxes).

const isExampleBox = (box: Box): boolean => box.isExample === true;

// Fabrique (et non constante) : de nouveaux ids à chaque seed, jamais partagés
// entre deux vies de la base.
const EXAMPLE_BOX_SEEDS = (): Box[] => [
  { id: uuidv4(), name: 'Petit carton', dimensions: { length: 20, width: 15, height: 10 }, unit: 'cm', visible: true, isExample: true },
  { id: uuidv4(), name: 'Grand carton', dimensions: { length: 40, width: 30, height: 25 }, unit: 'cm', visible: true, isExample: true },
];

// Réconcilie l'état « démo » de la base. Idempotente et sûre à appeler au
// montage comme après chaque changement de boîtes :
//  - base vide            → seed les 2 exemples (les exemples réapparaissent
//                           tant que la base reste vide) ;
//  - au moins une vraie   → supprime toute boîte d'exemple restante (dès que
//    boîte + un exemple     l'utilisateur a ses propres boîtes, plus d'exemples).
// Sur stockage bloqué, getAllBoxes() renvoie [] sans persister et saveBoxes()
// échoue silencieusement : dégradation gracieuse, jamais de crash.
export const reconcileExampleBoxes = (): void => {
  const boxes = getAllBoxes();
  if (boxes.length === 0) {
    saveBoxes(EXAMPLE_BOX_SEEDS());
    return;
  }
  const hasReal = boxes.some((box) => !isExampleBox(box));
  const hasExample = boxes.some(isExampleBox);
  if (hasReal && hasExample) {
    saveBoxes(boxes.filter((box) => !isExampleBox(box)));
  }
};

// Parse a numeric value that might use comma or dot as decimal separator.
// Returns NaN (not 0) for unparseable input so callers can reject the row.
// Ambiguous values (thousands separators, inner spaces, trailing junk) are
// rejected instead of silently mangled ("1,234.5" must not become 1.234).
const parseDecimalValue = (value: unknown): number => {
  if (value === null || value === undefined) return NaN;
  const normalized = value.toString().trim().replace(',', '.');
  if (normalized === '' || /\s/.test(normalized) || (normalized.match(/\./g) || []).length > 1) {
    return NaN;
  }
  return Number(normalized);
};

// Spreadsheet formula-injection guard: a cell starting with =, +, -, @, tab or
// CR would be executed by Excel/LibreOffice. Export prefixes it with a quote;
// import strips that quote back so export → import round-trips cleanly.
const guardFormula = (value: string): string => (/^[=+\-@\t\r]/.test(value) ? `'${value}` : value);
const unguardFormula = (value: string): string => (/^'[=+\-@\t\r]/.test(value) ? value.slice(1) : value);

// The template row emitted when exporting an empty database — not a real box.
const TEMPLATE_ROW = { id: 'example_box_id_1', name: 'Example Box Name' };

// Import CSV data to boxes
export const importBoxesFromCsv = (csvString: string): Box[] => {
  // Headers are normalized (trim + lowercase) so "ID" / " Name " work too.
  const tryParse = (delimiter: string) =>
    Papa.parse<any>(csvString, {
      header: true,
      delimiter,
      skipEmptyLines: true,
      transformHeader: (header: string) => header.trim().toLowerCase(),
    });

  // The semicolon fallback only kicks in when the expected columns are absent
  // (wrong delimiter). A single malformed ROW must never abort the whole
  // import — bad rows are simply skipped by the per-row validation below.
  const hasExpectedColumns = (result: { data: any[] }): boolean =>
    result.data.length > 0 &&
    ['length', 'width', 'height'].every((column) => column in (result.data[0] ?? {}));

  let parseResult;
  try {
    parseResult = tryParse('');
    if (!hasExpectedColumns(parseResult)) {
      const semicolon = tryParse(';');
      if (hasExpectedColumns(semicolon)) parseResult = semicolon;
    }
  } catch (error) {
    throw new Error('CSV parsing failed. Please check your file format.');
  }

  if (!hasExpectedColumns(parseResult)) {
    throw new Error('Failed to parse CSV file. Please check the format.');
  }

  const importedBoxes: Box[] = parseResult.data
    .filter(
      (row) =>
        !(
          row.id?.toString().trim() === TEMPLATE_ROW.id &&
          row.name?.toString().trim() === TEMPLATE_ROW.name
        )
    )
    .map((row) => {
      const visibleRaw =
        row.visible === undefined || row.visible === null
          ? ''
          : row.visible.toString().trim().toLowerCase();
      return {
        id: (row.id && unguardFormula(row.id.toString().trim())) || `box_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        name: row.name ? unguardFormula(row.name.toString().trim()) : '',
        dimensions: {
          length: parseDecimalValue(row.length),
          width: parseDecimalValue(row.width),
          height: parseDecimalValue(row.height),
        },
        unit: (typeof row.unit === 'string' && ['in', 'cm'].includes(row.unit.trim().toLowerCase()))
          ? (row.unit.trim().toLowerCase() as MeasurementUnit)
          : ('in' as MeasurementUnit),
        // Preserve visibility through export/import round-trips; default to
        // visible. Excel writes FALSE / VRAI / FAUX — accept them all.
        visible: visibleRaw === ''
          ? true
          : !['false', '0', 'faux', 'no', 'non'].includes(visibleRaw),
      };
    })
    .filter((box) => box.name && hasValidDimensions(box));

  // Duplicate ids inside one file: the last row wins (same rule as the merge
  // below), so the returned count matches what actually lands in the database.
  const byId = new Map<string, Box>();
  importedBoxes.forEach((box) => byId.set(box.id, box));
  const uniqueBoxes = Array.from(byId.values());

  if (uniqueBoxes.length === 0) {
    throw new Error('No valid box data found in the CSV file. Please check the format.');
  }

  // Update the database: replace boxes with the same id, add the others.
  const combinedBoxes = [...getAllBoxes()];
  uniqueBoxes.forEach((importedBox) => {
    const index = combinedBoxes.findIndex((box) => box.id === importedBox.id);
    if (index !== -1) combinedBoxes[index] = importedBox;
    else combinedBoxes.push(importedBox);
  });

  saveBoxes(combinedBoxes);
  return uniqueBoxes;
};

// Export boxes to CSV format
export const exportBoxesToCsv = (): string => {
  const boxes = getAllBoxes();
  const flattenedBoxes = boxes.map((box) => ({
    id: guardFormula(box.id),
    name: guardFormula(box.name),
    length: box.dimensions.length,
    width: box.dimensions.width,
    height: box.dimensions.height,
    unit: box.unit || 'in',
    visible: box.visible !== false,
  }));

  if (flattenedBoxes.length === 0) {
    return Papa.unparse([{ id: TEMPLATE_ROW.id, name: TEMPLATE_ROW.name, length: 12, width: 10, height: 8, unit: 'in', visible: true }]);
  }
  return Papa.unparse(flattenedBoxes);
};
